/**
 * useUserLocation - Custom Hook for Optimized Geolocation
 * 
 * Features:
 * - Instant location from cache on first render
 * - Background refresh of location
 * - Cache expiration (7 days)
 * - Automatic cache update when location changes
 * - Graceful error handling
 * - Prevents unnecessary rerenders
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCachedLocation,
  setCachedLocation,
  calculateDistance,
  getManualLocation,
  setManualLocation as saveManualLocation,
  createLocationSession,
  getLocationSession,
  clearLocationSession,
} from '../utils/locationStorage';

// Geolocation options for optimized performance
const GEO_OPTIONS = {
  enableHighAccuracy: false, // Set to false for faster results
  timeout: 10000, // 10 seconds timeout
  maximumAge: 300000, // Accept cached position up to 5 minutes old
};

const LOCATION_CHANGE_THRESHOLD_KM = 0.5; // 500 meters threshold for location change detection

/**
 * Custom hook for managing user location with caching
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableBackgroundRefresh - Whether to refresh location in background (default: true)
 * @param {number} options.thresholdKm - Distance threshold in km to trigger cache update (default: 0.5)
 * @param {Function} options.onLocationChange - Callback called when location changes (receives {latitude, longitude})
 * @returns {Object} - { location, loading, error, isFromCache, refreshLocation }
 */
const useUserLocation = (options = {}) => {
  const {
    enableBackgroundRefresh = true,
    thresholdKm = LOCATION_CHANGE_THRESHOLD_KM,
    onLocationChange,
  } = options;

  // State for location data
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);

  // Refs to prevent unnecessary rerenders and track mounted state
  const isMountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const cachedLocationRef = useRef(null);

  /**
   * Get current position using browser geolocation API
   * Returns a promise that resolves with position data
   */
  const getCurrentPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (err) => {
          switch (err.code) {
            case err.PERMISSION_DENIED:
              reject(new Error('Location permission denied'));
              break;
            case err.POSITION_UNAVAILABLE:
              reject(new Error('Location information unavailable'));
              break;
            case err.TIMEOUT:
              reject(new Error('Location request timed out'));
              break;
            default:
              reject(new Error('Unknown error occurred'));
          }
        },
        GEO_OPTIONS
      );
    });
  }, []);

  /**
   * Fetch fresh location from browser
   * Updates state and cache if successful
   */
  const fetchFreshLocation = useCallback(async (silent = false) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      const freshLocation = await getCurrentPosition();

      if (!isMountedRef.current) {
        return;
      }

      // Check if location changed significantly from cached
      const cached = cachedLocationRef.current;
      let shouldUpdateCache = true;

      if (cached) {
        const distance = calculateDistance(
          cached.latitude,
          cached.longitude,
          freshLocation.latitude,
          freshLocation.longitude
        );

        // Only update cache if location changed beyond threshold
        if (distance < thresholdKm) {
          shouldUpdateCache = false;
        }
      }

      // Update cache if needed
      if (shouldUpdateCache) {
        setCachedLocation(freshLocation.latitude, freshLocation.longitude);
        cachedLocationRef.current = {
          latitude: freshLocation.latitude,
          longitude: freshLocation.longitude,
          timestamp: Date.now(),
        };
      }

      setLocation({
        latitude: freshLocation.latitude,
        longitude: freshLocation.longitude,
      });
      setIsFromCache(false);
      setLoading(false);

      // Check if user manually set a location
      const manualLocation = getManualLocation();
      const hasManual = manualLocation !== null;
      
      // Check if this is a new session (app reopened) or same session
      const currentSession = getLocationSession();
      const isNewSession = currentSession === null;
      
      // If new session and manual is set, we should update to fresh location
      // If same session and manual is set, keep manual location
      const shouldUpdateContext = !hasManual || (hasManual && isNewSession);

      // Update context only if appropriate
      if (onLocationChange && shouldUpdateCache && shouldUpdateContext) {
        onLocationChange({
          latitude: freshLocation.latitude,
          longitude: freshLocation.longitude,
        });
        
        // If new session with manual set, update the manual location to current
        if (hasManual && isNewSession) {
          saveManualLocation(freshLocation.latitude, freshLocation.longitude);
        }
      }
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }

      // If we have cached data, use it as fallback
      const cached = cachedLocationRef.current || getCachedLocation();
      
      if (cached) {
        setLocation({
          latitude: cached.latitude,
          longitude: cached.longitude,
        });
        setIsFromCache(true);
        // Don't set error if we have fallback
        setLoading(false);
      } else {
        setError(err.message);
        setLoading(false);
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [getCurrentPosition, thresholdKm]);

  /**
   * Initialize location - use cache immediately, then refresh in background
   */
  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;

    // Create a new session on app mount
    createLocationSession();

    // Step 1: Try to get cached location immediately (synchronous, no delay)
    // Priority: manual location > cached location
    const manual = getManualLocation();
    const cached = manual || getCachedLocation();
    
    if (cached) {
      cachedLocationRef.current = cached;
      setLocation({
        latitude: cached.latitude,
        longitude: cached.longitude,
      });
      setIsFromCache(true);
      setLoading(false);
    }

    // Step 2: If background refresh is enabled, fetch fresh location silently
    if (enableBackgroundRefresh) {
      // Small delay to not block initial render
      const timer = setTimeout(() => {
        fetchFreshLocation(true); // true = silent mode (don't show loading)
      }, 100);

      return () => {
        clearTimeout(timer);
        isMountedRef.current = false;
      };
    } else {
      isMountedRef.current = false;
    }

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
    };
  }, [enableBackgroundRefresh, fetchFreshLocation]);

  /**
   * Manual refresh function - can be called by user or component
   */
  const refreshLocation = useCallback(() => {
    return fetchFreshLocation(false);
  }, [fetchFreshLocation]);

  /**
   * Set manual location - call this when user manually selects a location
   * This will prevent auto-update from overwriting it during this session
   */
  const setManualLocation = useCallback((latitude, longitude) => {
    saveManualLocation(latitude, longitude);
    clearLocationSession(); // Clear session so this becomes the "manual" location
  }, []);

  return {
    location,
    loading,
    error,
    isFromCache,
    refreshLocation,
    setManualLocation,
  };
};

export default useUserLocation;
